import { Button } from "@dune/ui";
import { formatCurrency } from "@dune/utils";
import { AlertTriangle } from "lucide-react";
import React from "react";

interface ConstructionCostValidationInlineProps {
	onModifyCost: () => void;
	onCancel: () => void;
	onKeepCost?: () => void;
	totalDeclaredAmount: number;
	constructionCost: number;
	errorCode?: string;
}

const ConstructionCostValidationInline: React.FC<
	ConstructionCostValidationInlineProps
> = ({
	onModifyCost,
	onCancel,
	onKeepCost,
	totalDeclaredAmount,
	constructionCost,
	errorCode,
}) => {
	const isLowAmount = errorCode === "DECLARED_AMOUNT_TOO_LOW";
	const isExceededDirect = errorCode === "CONSTRUCTION_COST_EXCEEDED";
	const isExceededWithValidation =
		errorCode === "CONSTRUCTION_COST_VALIDATION_REQUIRED";

	return (
		<div className="bg-white">
			<h3 className="text-lg font-semibold text-gray-900 mb-4">
				{isLowAmount
					? "Montants différents"
					: "Validation du coût de construction"}
			</h3>

			<div className="space-y-4">
				{isLowAmount && (
					<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
							<div className="flex-1">
								<h4 className="font-medium text-orange-800 mb-1">
									Montant déclaré inférieur
								</h4>
								<p className="text-sm text-orange-700">
									Attention le montant des lots déclarés (
									{formatCurrency(totalDeclaredAmount)}) est inférieur au coût
									de construction prévisionnel indiqué pour la tarification (
									{formatCurrency(constructionCost)}). Souhaitez-vous modifier
									le coût de construction prévisionnel ?
								</p>
							</div>
						</div>
					</div>
				)}

				{isExceededDirect && (
					<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
							<div className="flex-1">
								<h4 className="font-medium text-orange-800 mb-1">
									Montant déclaré supérieur
								</h4>
								<p className="text-sm text-orange-700">
									Attention le montant des lots déclarés est supérieur au coût
									de construction prévisionnel indiqué pour la tarification et
									ce nouveau coût de construction entraîne une modification de
									celle-ci. Une nouvelle indication tarifaire est nécessaire
									pour soumettre votre dossier
								</p>
							</div>
						</div>
					</div>
				)}

				{isExceededWithValidation && (
					<div className="bg-gray-50 rounded-lg p-4 space-y-2">
						<div className="flex justify-between">
							<span className="font-medium">Total des montants déclarés :</span>
							<span className="font-bold text-gray-900">
								{formatCurrency(totalDeclaredAmount)}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-medium">
								Coût de construction prévisionnel :
							</span>
							<span className="font-bold text-gray-900">
								{formatCurrency(constructionCost)}
							</span>
						</div>
						<div className="flex justify-between text-orange-600">
							<span className="font-medium">Dépassement :</span>
							<span className="font-bold">
								{formatCurrency(totalDeclaredAmount - constructionCost)}
							</span>
						</div>
					</div>
				)}

				{isExceededWithValidation && (
					<>
						<p>
							<strong>
								Le montant total déclaré par les intervenants dépasse le coût de
								construction prévisionnel.
							</strong>
						</p>

						<p>
							Pour garantir la conformité de votre dossier, nous devons vérifier
							si cette modification impacte la tarification de votre offre
							d'assurance.
						</p>

						<p className="text-sm text-gray-600">
							Les informations et documents déjà saisis restent sauvegardés.
						</p>
					</>
				)}
			</div>

			<div className="flex justify-end mt-6">
				{isLowAmount ? (
					<div className="grid grid-cols-2 gap-3 w-full">
						<Button variant="outline" className="w-full" onClick={onKeepCost}>
							Conserver le coût de construction prévisionnel
						</Button>
						<Button variant="default" className="w-full" onClick={onModifyCost}>
							Modifier le coût de construction
						</Button>
					</div>
				) : isExceededDirect ? (
					<Button variant="default" onClick={onModifyCost}>
						Modifier le coût de construction
					</Button>
				) : isExceededWithValidation ? (
					<div className="grid grid-cols-2 gap-3 w-full">
						<Button variant="outline" className="w-full" onClick={onCancel}>
							Annuler
						</Button>
						<Button variant="default" className="w-full" onClick={onModifyCost}>
							Modifier le coût
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ConstructionCostValidationInline;
